import { FC, useMemo } from 'react';
import useTheme from '../../../hooks/ui/useTheme';
import { OpeningHoursInterface } from '../../../services/exports/Interfaces';
import { useTranslation } from 'react-i18next';
import FeedbackLabel from '../../labels/FeedbackLabel';
import WorkScheduleHelpers from '../../../services/helpers/WorkScheduleHelpers';
import i18n from 'i18next';
import { useSelector } from 'react-redux';
import useBasket from '../../../hooks/basket/useBasket';
import { StoreInterface } from '../../../store/types';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import CounterButton from '../../buttons/CounterButton';
import PriceComparison from '../../menu/PriceComparison';
import OfferLineBreakdown from '../../../features/offers/components/OfferLineBreakdown';
import { IOrderBasketItem, IOrderBasketItemContent } from '.';
import classnames from 'classnames';

const OrderBasketItem: FC<IOrderBasketItem & IOrderBasketItemContent> = (props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Basket:OrderBasketItem' });

  const { company } = useSelector((store: StoreInterface) => store.initialData);

  const { data, index, hideDiscount, editable, extra, onUpdateClick, renderDescription, getSoldOutItems, classNames } =
    props;

  const theme = useTheme();
  const { formatScheduleUnit } = useAppMethods();
  const { incrementItem, decrementItem } = useBasket();

  const renderWarnings = useMemo(() => {
    if (!editable) {
      return null;
    }

    if (data.is_sold_out) {
      const soldOutItems = getSoldOutItems();

      return (
        <FeedbackLabel
          message={t('feedback.item_sold_out', {
            count: soldOutItems.count(),
            items: soldOutItems.implode(', '),
          })}
          type="Notice"
          textClassname="text-xs"
        />
      );
    }

    return (
      !data.is_available && (
        <FeedbackLabel
          message={t('feedback.item_unavailable', {
            schedule: WorkScheduleHelpers.formatSchedule(
              data?.category?.schedule ?? data?.menu?.schedule ?? company?.work_schedule,
              (item: OpeningHoursInterface) => `${formatScheduleUnit(item.open)} - ${formatScheduleUnit(item.close)}`,
              (item: OpeningHoursInterface) =>
                `${formatScheduleUnit(item.open, { day: true, time: true })} - ${formatScheduleUnit(item.close, { day: true, time: true })}`,
            ),
          })}
          type="Notice"
          textClassname="text-xs"
        />
      )
    );
  }, [editable, data, company?.work_schedule, i18n.language]);

  const description = renderDescription();

  return (
    <div className={classnames(itemContainer, classNames?.itemContainer)}>
      <div className="flex items-start gap-sm">
        {editable ? (
          <CounterButton
            count={data.quantity}
            increase={() => incrementItem(index)}
            decrease={() => decrementItem(index)}
            allowZero
            iconColor={theme.color.brand.icon.color}
            classes={{
              container: classnames(counterContainer, classNames?.counterContainer),
              quantity: counterQuantity,
              buttons: counterButtons,
            }}
          />
        ) : (
          <span className={nonEditableQuantity}>{data.quantity}x</span>
        )}
        <div className="flex-1 min-w-0 space-y-2xs">
          <p
            className="break-words line-clamp-2 text-sm font-semibold text-brand-text-primary"
            style={{ wordBreak: 'break-word' }}
          >
            {data.entry?.name}
          </p>
          {description}
          {data.note && <p className="text-xs text-muted-foreground break-words">{data.note}</p>}
          {editable && onUpdateClick && (
            <button
              type="button"
              onClick={onUpdateClick}
              className="block w-fit whitespace-nowrap text-left text-xs text-brand-primary font-medium hover:underline"
            >
              {t('update')}
            </button>
          )}
        </div>
        {/* Right-align the price column: with a wide breakdown line ("1 × … paid · 1 × free") the
            column grows wider than the price row, so the price row must align to the end (right)
            rather than sitting at the column's left edge (founder 2026-08-06). */}
        <div className="shrink-0 flex flex-col items-end space-y-xs">
          <PriceComparison
            className="text-muted-foreground"
            originalPrice={data.original_price}
            actualPrice={data.actual_price}
            hideDiscount={hideDiscount}
            originalPriceStyle={{
              fontSize: theme.font.size.sm,
            }}
            actualPriceStyle={{
              fontSize: theme.font.size.sm,
              fontWeight: theme.font.weight.semibold,
              color: 'black',
            }}
          />
          <OfferLineBreakdown item={data} className="text-right" />
          {!!data.badges?.length && (
            <div className="flex flex-wrap justify-end gap-xs">
              {data.badges.map((badge) => (
                <p key={badge} className={badgeStyle}>
                  {badge}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
      {renderWarnings}
      {extra}
    </div>
  );
};

export default OrderBasketItem;

const itemContainer = 'w-full rounded-xl bg-brand-card border-xs border-brand-border p-sm space-y-sm';

const counterContainer = '!w-fit !min-w-0 h-fit flex items-center border-xs p-xs shrink-0';

const counterQuantity = 'text-sm font-semibold px-sm text-brand-text-primary tabular-nums';

const counterButtons = 'h-4 w-4';

const nonEditableQuantity = 'text-sm font-semibold text-brand-primary shrink-0 mt-2xs';

const badgeStyle =
  'w-fit bg-brand-primary/30 text-xs px-xs py-2xs rounded flex items-center justify-center text-brand-primary font-medium';
